import { type FC, useState, useEffect, useCallback } from "react";

const LABEL_FILES: ReadonlyArray<{ name: string; label: string; shelcodeCount: number }> = [
  { name: '30572-single', label: '30572 Single',    shelcodeCount: 1 },
  { name: '30572-dual',   label: '30572 Dual',      shelcodeCount: 2 },
  { name: '30333-main',   label: '30333 Main (2×)', shelcodeCount: 2 },
  { name: '30333-quad',   label: '30333 Quad (4×)', shelcodeCount: 4 },
];

export const LabelPreview: FC = () => {
  const [labelName, setLabelName] = useState(LABEL_FILES[0]!.name);
  const [shelcodes, setShelcodes] = useState<string[]>(['']);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [compiling, setCompiling] = useState(false);
  const [compileError, setCompileError] = useState<string | null>(null);

  const selected = LABEL_FILES.find(l => l.name === labelName) ?? LABEL_FILES[0]!;

  const handlePreview = useCallback(() => {
    setCompileError(null);
    setCompiling(true);
    const inputs = Array.from({ length: selected.shelcodeCount }, (_, i) => shelcodes[i] ?? '');
    import('../label-compiler').then(({ compileLabelToSvg }) =>
      compileLabelToSvg(labelName, inputs)
    ).then(svg => {
      setSvgContent(svg);
    }).catch((e: unknown) => {
      setCompileError(`Compilation failed: ${String(e)}`);
    }).finally(() => {
      setCompiling(false);
    });
  }, [labelName, selected.shelcodeCount, shelcodes]);

  // Kick off compiler warm-up in the background when the page first loads.
  useEffect(() => {
    import('../label-compiler').then(({ preloadCompiler }) => preloadCompiler()).catch(() => undefined);
  }, []);

  return (
    <div style={{ padding: '6px' }}>
      <h3 style={{ margin: '0 0 8px' }}>Label preview</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '400px' }}>
        <label>
          Label:{' '}
          <select value={labelName} onChange={e => { setLabelName(e.target.value); setSvgContent(null); }}>
            {LABEL_FILES.map(l => <option key={l.name} value={l.name}>{l.label}</option>)}
          </select>
        </label>
        {Array.from({ length: selected.shelcodeCount }, (_, i) => (
          <label key={i}>
            Shelcode {i + 1}:{' '}
            <input
              type="text"
              value={shelcodes[i] ?? ''}
              onChange={e => setShelcodes(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
              placeholder="5410000013"
              style={{ fontFamily: 'monospace' }}
            />
          </label>
        ))}
        <button type="button" onClick={handlePreview} disabled={compiling}>
          {compiling ? 'Compiling…' : 'Preview'}
        </button>
      </div>
      {compileError && <p style={{ color: 'red', marginTop: '8px', whiteSpace: 'pre-wrap' }}>{compileError}</p>}
      {svgContent !== null && !compiling && (
        <div
          data-testid="label-preview-svg"
          aria-label="Label preview"
          style={{ marginTop: '12px', border: '1px solid #ccc', display: 'inline-block', background: '#fff', padding: '4px' }}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      )}
    </div>
  );
};
